/**
 * TopSpotsPage — pixelgenau nach top-spots.html Prototyp.
 *
 * Route: /top-spots (Auth)
 * Table-Liste (Rank/Hotspot/City/HNT), Zeitraum-Select 24h/7d/30d.
 * Nur verifizierte, per Zeitraum aggregierte Werte — nie Lifetime.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useTopSpots } from './useTopSpots'
import { useHotspots } from '../hotspots/useHotspots'
import { fmt } from '../../lib/fmt'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'

const TIME_RANGES: ('24h' | '7d' | '30d')[] = ['24h', '7d', '30d']

export function TopSpotsPage() {
  const { t } = useTranslation('topstats')

  // FIN-SWEEP (shell-dashboard): document.title wird zentral in __root.tsx via
  // lib/seo.ts (ROUTE_KEY_MAP für /top-spots) gesetzt und reagiert dort bereits
  // auf Sprachwechsel. Ein lokaler, hartkodiert-englischer Override hier hätte
  // diesen globalen, i18n-fähigen Mechanismus nach dem Mount überschrieben.

  const [range, setRange] = useState<'24h' | '7d' | '30d'>('24h')
  const { data: spots, isLoading, isError } = useTopSpots(range)

  // Eigene/beobachtete Hotspots kommen aus dem bestehenden /api/v2/hotspots
  // (kein neuer Endpoint): default-Fetch liefert Mine + Watched, `watched`-Flag
  // unterscheidet. Adressabgleich gegen die netzwerkweite Top-Spots-Liste
  // liefert die "yours"/"watched"-Badges aus dem Prototyp.
  const { data: myHotspots } = useHotspots({ per_page: 200 })
  const mineByAddress = new Map<string, boolean>()
  for (const h of myHotspots ?? []) {
    if (h.address) mineByAddress.set(h.address, h.watched === true)
  }

  return (
    <div data-testid="top-spots-page">
      <div className="section-header">
        <div>
          <h1>{t('spots.title')}</h1>
          <p className="muted">{t('spots.subtitle')}</p>
        </div>
        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label={t('spots.rangeLabel')}
          value={range}
          onChange={(e) => setRange(e.target.value as '24h' | '7d' | '30d')}
          data-testid="top-spots-range"
        >
          {TIME_RANGES.map((r) => (
            <option key={r} value={r}>
              {t(`spots.rangeOptions.${r}`)}
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="card" data-testid="top-spots-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ts-row skeleton" style={{ height: 48 }} />
          ))}
        </div>
      )}

      {isError && (
        <div className="card card-pad state-error" data-testid="top-spots-error">
          <p>{t('spots.error')}</p>
        </div>
      )}

      {!isLoading && !isError && Array.isArray(spots) && spots.length === 0 && (
        <div className="card card-pad state-empty" data-testid="top-spots-empty">
          <p>{t('spots.empty')}</p>
        </div>
      )}

      {!isLoading && !isError && Array.isArray(spots) && spots.length > 0 && (
        <div className="card" data-testid="top-spots-list">
          <style>{`
            .ts-row{display:grid;grid-template-columns:44px minmax(160px,1.3fr) minmax(110px,1fr) 120px 110px;gap:12px;align-items:center;padding:12px 16px;border-bottom:1px solid var(--border);font-size:13px}
            .ts-row:last-child{border-bottom:0}
            .ts-head{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;color:var(--text-subtle);border-bottom:1px solid var(--border)}
            .ts-num{font-family:var(--font-mono);font-variant-numeric:tabular-nums;text-align:right}
            .ts-rank{width:32px;height:32px;border-radius:9px;display:grid;place-items:center;font-family:var(--font-mono);font-weight:700;font-size:13px;background:var(--surface-2);color:var(--text-muted)}
            .ts-rank.top{background:var(--gradient-brand);color:#fff}
            @media (max-width:700px){.ts-row{grid-template-columns:44px 1fr 100px}.ts-col-md{display:none}}
          `}</style>
          <div className="ts-row ts-head">
            <span />
            <span>{t('spots.columns.hotspot')}</span>
            <span>{t('spots.columns.city')}</span>
            <span className="ts-num">{t('spots.hntRange', { range })}</span>
            <span className="ts-num ts-col-md">{t('spots.gbRange', { range })}</span>
          </div>
          {spots.map((h, i) => {
            const value = range === '24h' ? h.rewards_24h : range === '7d' ? h.rewards_7d : h.rewards_30d
            const own = mineByAddress.get(h.address)
            const isMine = own === false
            const isWatched = own === true
            const displayCity = h.city?.name ?? t('spots.unknownCity')
            return (
              <Link
                key={h.address}
                to="/hotspots/$id"
                params={{ id: String(h.id) }}
                className="ts-row"
                data-testid={`top-spots-row-${i}`}
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  ...(isMine ? { background: 'color-mix(in oklch, var(--primary-soft) 35%, transparent)' } : {}),
                }}
              >
                <div className={`ts-rank${i < 3 ? ' top' : ''}`}>{i + 1}</div>
                <div>
                  <span style={{ fontWeight: 500 }}>
                    {hotspotDisplayName(h.name, h.address, t('spots.unnamedHotspot'))}
                  </span>{' '}
                  {isMine && <span className="pill pill-primary" style={{ fontSize: 10 }}>{t('spots.badges.mine')}</span>}
                  {isWatched && <span className="pill pill-accent" style={{ fontSize: 10 }}>{t('spots.badges.watched')}</span>}
                </div>
                <span className="muted">{displayCity}</span>
                <span className="ts-num">{fmt(value ?? 0)}</span>
                {/* GB·Zeitraum: kein per-Hotspot-Datenfeld im TopSpot-Schema (nur
                    HNT-Rewards), Datenvolumen wird backendseitig nur fleet-weit
                    aggregiert (dashboard-Endpoint). Ohne neuen Backend-Endpunkt
                    (Hard-Regel) bleibt die Spalte strukturell/i18n-korrekt, aber
                    mit Platzhalter — reale Werte sobald TopSpot.gb_{range} existiert. */}
                <span className="ts-num ts-col-md" data-testid={`top-spots-gb-${i}`}>—</span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
